/*
 * Copyright (c) 2019-2026  Apricad LLC. All Rights Reserved.
 */

import axios from "axios";
import { Action, Dispatch } from "redux";
import {
  sProfileOk,
  sProfileFail,
  sDeleteUserOk,
  sDeleteUserFail,
} from "assets/jss/views/ResStrings";

import {
  FETCH_USER,
  CLASSIC_LOGIN,
  UPDATE_USER,
  UPDATE_USER_FAILED,
  DELETE_USER_FAILED,
  SHOW_TOAST,
  PHOENIX_NOP,
  ApricotActionCreator,
  reqHeader,
} from "./types";

// when redux-thunk see we're returning a function in action creator
// it gives us the dispatch function, so we can decide when to dispatch
// (without redux-thunk, the dispatch to reducer is done beyond our control)

export const fetchUser: ApricotActionCreator =
  () =>
  async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
    api = api || axios;
    const res = await api.get("/auth/current_user");
    return dispatch({ type: FETCH_USER, payload: res.data });
  };

export const logout: ApricotAction =
  () =>
  async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
    api = api || axios;
    await api.get("/auth/logout");
    window.location.href = "/";
    return dispatch({ type: FETCH_USER, payload: null });
  };

export const classicLogin: ApricotAction =
  (email: string, name?: string) =>
  async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
    api = api || axios;
    // Passport-Local require 'password', so give a dummy one
    const res = await api({
      method: "post",
      url: "/auth/login",
      headers: reqHeader(getState),
      data: { email, password: "acpw", name },
    });

    // "name" is only provided in SignUp, before email confirmation. Other than that case, we
    // navigate to a default place (decided by server via res.data) if this user is in our DB.
    if (name === undefined) {
      window.location.href = res.data;
    }
    // window.location.reload(); // enforce reloading

    return dispatch({ type: CLASSIC_LOGIN, payload: res.data });
  };

export const updateUser: ApricotAction =
  (
    uid: string,
    data: any,
    showToast: boolean = true,
    updateStore: boolean = true,
  ) =>
  async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
    try {
      api = api || axios;
      await api({
        method: "put",
        url: "/api/user/" + uid,
        headers: reqHeader(getState),
        data,
      });

      if (showToast) {
        dispatch({
          type: SHOW_TOAST,
          payload: { type: "normal", message: sProfileOk },
        });
      }
      return dispatch(
        updateStore
          ? { type: UPDATE_USER, payload: data }
          : { type: PHOENIX_NOP },
      );
    } catch (err) {
      dispatch({
        type: SHOW_TOAST,
        payload: { type: "warn", message: sProfileFail },
      });
      return dispatch({ type: UPDATE_USER_FAILED, payload: null });
    }
  };

export const deleteUser: ApricotAction =
  (uid: string) =>
  async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
    try {
      api = api || axios;
      await api.delete("/api/user/" + uid);
      await api.get("/auth/logout");
      window.location.href = "/";

      dispatch({
        type: SHOW_TOAST,
        payload: { type: "normal", message: sDeleteUserOk },
      });

      // then dispatch the same result as logout
      return dispatch({ type: FETCH_USER, payload: null });
    } catch (err) {
      dispatch({
        type: SHOW_TOAST,
        payload: { type: "warn", message: sDeleteUserFail },
      });
      return dispatch({ type: DELETE_USER_FAILED, payload: null });
    }
  };

export type ApricotAction = ApricotActionCreator;
